import {
  SURVEY_DOCUMENT_VALIDITY_MONTHS_MAX,
  SURVEY_DOCUMENT_VALIDITY_MONTHS_MIN,
  type SurveyDeadlineMode,
} from '../types/survey-invite';
import { addMonthsToIsoDay } from './deadline-dates';

const SURVEY_DEADLINE_TIMEZONE = 'Europe/Rome';

const romeDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: SURVEY_DEADLINE_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Giorno di calendario `YYYY-MM-DD` dell'istante in Europe/Rome (parità con `SurveyPdfService`). */
function toRomeIsoDay(instant: Date): string {
  const parts = romeDayFormatter.formatToParts(instant);
  const pick = (type: 'year' | 'month' | 'day') =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

export interface SurveyDocumentDueAtInput {
  deadlineMode: SurveyDeadlineMode;
  documentValidityMonths: number | null;
  signedAt: Date;
}

export interface SurveyDocumentDueAt {
  dueAt: Date | null;
  validityMonths: number | null;
}

/**
 * Scadenza del documento prodotto da un survey (spec §5.3). `NONE` e template
 * incoerenti (mesi assenti o non interi positivi, impossibili col CHECK del DB) non
 * producono scadenza: il chiamante non crea nulla nello scadenzario.
 */
export function computeSurveyDocumentDueAt(input: SurveyDocumentDueAtInput): SurveyDocumentDueAt {
  const months = input.documentValidityMonths;
  if (
    input.deadlineMode === 'NONE' ||
    months === null ||
    !Number.isInteger(months) ||
    months <= 0
  ) {
    return { dueAt: null, validityMonths: null };
  }
  const dueDay = addMonthsToIsoDay(toRomeIsoDay(input.signedAt), months);
  return {
    dueAt: new Date(`${dueDay}T00:00:00.000Z`),
    validityMonths: input.deadlineMode === 'RECURRING' ? months : null,
  };
}

export interface SurveyDeadlineConfig {
  deadlineMode: SurveyDeadlineMode;
  documentValidityMonths: number | null;
}

export interface SurveyDeadlineConfigPatch {
  deadlineMode?: SurveyDeadlineMode;
  documentValidityMonths?: number | null;
}

export type SurveyDeadlineConfigError =
  | 'deadline_mode_requires_validity_months'
  | 'deadline_mode_none_excludes_validity_months'
  | 'validity_months_out_of_range';

export type SurveyDeadlineConfigResolution =
  | { ok: true; config: SurveyDeadlineConfig }
  | { ok: false; error: SurveyDeadlineConfigError };

/** Modo di un template creato o importato con i soli mesi (parità col backfill: una validità rinnovava). */
export function deadlineModeForValidityMonths(
  documentValidityMonths: number | null,
): SurveyDeadlineMode {
  return documentValidityMonths === null ? 'NONE' : 'RECURRING';
}

function modeAfterMonthsOnlyPatch(
  currentMode: SurveyDeadlineMode,
  months: number | null,
): SurveyDeadlineMode {
  if (months === null) {
    return 'NONE';
  }
  return currentMode === 'NONE' ? 'RECURRING' : currentMode;
}

function isValidityMonthsInRange(months: number): boolean {
  return (
    Number.isInteger(months) &&
    months >= SURVEY_DOCUMENT_VALIDITY_MONTHS_MIN &&
    months <= SURVEY_DOCUMENT_VALIDITY_MONTHS_MAX
  );
}

function datedConfig(
  deadlineMode: Exclude<SurveyDeadlineMode, 'NONE'>,
  months: number,
): SurveyDeadlineConfigResolution {
  if (!isValidityMonthsInRange(months)) {
    return { ok: false, error: 'validity_months_out_of_range' };
  }
  return { ok: true, config: { deadlineMode, documentValidityMonths: months } };
}

/**
 * Stato risultante di `deadlineMode` + mesi dopo una patch parziale, nel rispetto del
 * CHECK `survey_template_deadline_mode_coherent`: `NONE` ⇔ mesi null, altrimenti mesi
 * interi da 1 a 120. Le scritture che impostano i soli mesi (API storica) restano valide.
 * È l'unico punto che decide la coerenza: lo usano la patch del template e la scadenza
 * personalizzata di una richiesta al cliente (`resolveEffectiveSurveyDeadlineConfig`).
 */
export function resolveSurveyDeadlineConfig(
  current: SurveyDeadlineConfig,
  patch: SurveyDeadlineConfigPatch,
): SurveyDeadlineConfigResolution {
  const months =
    patch.documentValidityMonths !== undefined
      ? patch.documentValidityMonths
      : current.documentValidityMonths;

  if (patch.deadlineMode === undefined) {
    const deadlineMode = modeAfterMonthsOnlyPatch(current.deadlineMode, months);
    if (deadlineMode === 'NONE' || months === null) {
      return { ok: true, config: { deadlineMode: 'NONE', documentValidityMonths: null } };
    }
    return datedConfig(deadlineMode, months);
  }
  if (patch.deadlineMode === 'NONE') {
    if (patch.documentValidityMonths !== undefined && patch.documentValidityMonths !== null) {
      return { ok: false, error: 'deadline_mode_none_excludes_validity_months' };
    }
    return { ok: true, config: { deadlineMode: 'NONE', documentValidityMonths: null } };
  }
  if (months === null) {
    return { ok: false, error: 'deadline_mode_requires_validity_months' };
  }
  return datedConfig(patch.deadlineMode, months);
}

/**
 * Scadenza con cui il PDF di una richiesta entra nello scadenzario: quella scelta per il
 * cliente sull'item (`SurveyInviteItem.deadlineMode` + mesi) quando c'è, altrimenti quella del
 * template. Passa dallo stesso `resolveSurveyDeadlineConfig` della patch del template; un
 * override incoerente (impossibile col CHECK `survey_invite_item_deadline_override_coherent`)
 * ricade sul template.
 */
export function resolveEffectiveSurveyDeadlineConfig(
  template: SurveyDeadlineConfig,
  override: SurveyDeadlineConfig | null,
): SurveyDeadlineConfig {
  if (override === null) {
    return { ...template };
  }
  const resolution = resolveSurveyDeadlineConfig(template, override);
  return resolution.ok ? resolution.config : { ...template };
}

export function isSameSurveyDeadlineConfig(
  first: SurveyDeadlineConfig,
  second: SurveyDeadlineConfig,
): boolean {
  return (
    first.deadlineMode === second.deadlineMode &&
    first.documentValidityMonths === second.documentValidityMonths
  );
}
